//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { useInstagramMessagesAccess } from '@account-settings/hooks/useInstagramMessagesAccess';
import {
  RIGHTS_STATUS,
  RightsRequestCommunicationType,
} from '@apis/rights-management/rightsManagementApiModels';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  RIGHT_STATUSES_READ_ONLY_FORM,
} from '@uia/rights/constants/uiaRightsConstants';
import { useUIARightsRequestContext } from '@uia/rights/context/UIARightsRequestContext';
import { ExtendedRightsRequest } from '@uia/rights/forms/ExtendedRightsRequest';
import { RequestRequiredForMissingTCUrl } from '@uia/rights/forms/RequestRequiredForMissingTCUrl';
import { useGenerativeAiRightsRequestQuery } from '@uia/rights/hooks/useGenerativeAiRightsRequestQuery';
import { useHasInstagramManageMessagesFeatureAndPermissions } from '@uia/rights/hooks/useHasInstagramManageMessagesFeatureAndPermissions';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { useRequestRightsInAuthorizedUrl } from '@uia/rights/hooks/useRequestRightsInAuthorizedUrl';
import {
  getRequestMetadata,
  getRightsRequestDetailsWithSyndicationCheck,
} from '@uia/rights/utils/rightsRequestUtils';
import { copyTextToClipboard } from '@utils/copyToClipboard';

export interface InstagramRequestViaCommentsProps {
  approvalPhrase: string | undefined;
  cuid: Cuid;
  isReRequestRequiredForMissingTCUrl: boolean;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  setRightsRequestText: (value: string) => void;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const InstagramRightsRequest = ({
  approvalPhrase,
  cuid,
  isReRequestRequiredForMissingTCUrl,
  requestType,
  rightsRequestText,
  setRightsRequestText,
  setSelectedOption,
}: InstagramRequestViaCommentsProps) => {
  const requestRightsInIgUrl = useRequestRightsInAuthorizedUrl();

  const { hasInstagramManageMessagesFeatureAndPermissions } =
    useHasInstagramManageMessagesFeatureAndPermissions();

  const { data: { accountsWithAccess = [] } = {} } =
    useInstagramMessagesAccess();

  const hasInstagramMessagesAccess = accountsWithAccess.length > 0;

  const { requestTextIndex, requestTextSource, selectedRightsOption } =
    useUIARightsRequestContext();

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: hasFormErrors,
    isError: isErrorFormErrors,
    isLoading: isLoadingFormErrors,
  } = useHasRightsFormErrorsQuery(rightsRequestText);

  const { trackEvent } = useMediaTrackingContext();

  const { data: aiGeneratedMessage } = useGenerativeAiRightsRequestQuery({
    approvalPhrase,
    cuid,
    rightsRequestText,
  });

  if (
    isLoadingMetadata ||
    isErrorMetadata ||
    isLoadingFormErrors ||
    isErrorFormErrors
  ) {
    return null;
  }

  const { networkDMUrl, originalUrl } = metadata;

  const {
    approvalPhrase: previousApprovalPhrase,
    communicationType,
    status,
  } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  const getDMRequestMessage = (originalText: string) =>
    originalUrl && !originalText.includes(originalUrl)
      ? `${originalUrl}\n${originalText}`
      : originalText;

  const onClickCopyPaste = async () => {
    if (!rightsRequestText) {
      return;
    }

    trackEvent({
      cuid,
      event: MediaTrackingEventName.RightsRequested,
    });

    const communicationType =
      requestType === RightsRequestOptions.COMMENTS
        ? RightsRequestCommunicationType.COMMENT
        : RightsRequestCommunicationType.DM;

    const requestMessage =
      requestType === RightsRequestOptions.COMMENTS
        ? rightsRequestText
        : getDMRequestMessage(rightsRequestText);

    const targetUrl =
      requestType === RightsRequestOptions.COMMENTS
        ? originalUrl
        : networkDMUrl;

    if (requestMessage !== rightsRequestText) {
      setRightsRequestText(requestMessage);
    }

    await copyTextToClipboard(requestMessage);

    const metadata = getRequestMetadata(
      rightsRequestText,
      requestTextIndex,
      requestTextSource,
      aiGeneratedMessage,
      selectedRightsOption,
    );

    return requestRightsInIgUrl({
      approvalPhrase,
      communicationType,
      cuid,
      requestMessage,
      targetUrl,
      requestMessageSource: metadata.requestMessageSource,
      requestMessageLanguage: metadata.requestMessageLanguage,
    });
  };

  const isReadOnlyForm = RIGHT_STATUSES_READ_ONLY_FORM.includes(status);

  const isCommentsRequest = requestType === RightsRequestOptions.COMMENTS;

  const shouldDisableCopyPasteInDmButton =
    hasFormErrors ||
    !hasInstagramManageMessagesFeatureAndPermissions ||
    !hasInstagramMessagesAccess;

  const shouldDisableCopyPasteButton = isCommentsRequest
    ? hasFormErrors
    : shouldDisableCopyPasteInDmButton;

  return (
    <Wrapper>
      {status !== RIGHTS_STATUS.EXPIRED ? (
        <>
          {!isReRequestRequiredForMissingTCUrl && !isReadOnlyForm && (
            <FlexStart>
              <RequiredPermissionsButton
                isDisabled={shouldDisableCopyPasteButton}
                onClick={onClickCopyPaste}
                permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
                trackingId={
                  requestType === RightsRequestOptions.COMMENTS
                    ? UIATrackingIds.RIGHTS_REQUEST_COMMENT_REQUEST_BUTTON
                    : UIATrackingIds.RIGHTS_REQUEST_DIRECT_MESSAGE_BUTTON
                }
              >
                Copy and Paste in&nbsp;
                {requestType === RightsRequestOptions.COMMENTS
                  ? 'Instagram'
                  : 'Direct Message'}
              </RequiredPermissionsButton>
              <>
                <CaptionSecondaryText>
                  This will open Instagram in a new window. Make sure you’re
                  logged into the correct Instagram account before posting&nbsp;
                  {requestType === RightsRequestOptions.COMMENTS
                    ? 'the comment'
                    : 'in direct message'}
                  .
                </CaptionSecondaryText>
                <PrimaryAnchor
                  href={MANAGE_RIGHTS_REQUEST_ARTICLE_URL}
                  target='_blank'
                  trackingId={
                    UIATrackingIds.RIGHTS_REQUEST_MANAGE_RIGHTS_LEARN_MORE_LINK
                  }
                >
                  Learn more
                </PrimaryAnchor>
              </>
            </FlexStart>
          )}

          <RequestRequiredForMissingTCUrl
            cuid={cuid}
            isReRequestRequiredForMissingTCUrl={
              isReRequestRequiredForMissingTCUrl
            }
            requestType={requestType}
            setRightsRequestText={setRightsRequestText}
            setSelectedOption={setSelectedOption}
          />
        </>
      ) : (
        <ExtendedRightsRequest
          communicationType={communicationType}
          cuid={cuid}
          previousApprovalPhrase={previousApprovalPhrase}
          rightsRequestText={rightsRequestText}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
  `,
);

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
